// Centring the planner's map on a warning picked from the "Warnings on route"
// sheet: the hazard in the middle, zoomed in to at least street-ish level so
// its marker is there (markers start at z9), never zoomed out. A hazard whose
// details panel the map follows goes in the middle of the map left above the
// panel instead (a phone's bottom sheet).

import { followingFocusOffset } from '../trailHazards/hazardPanelPlacement';

export const FOCUS_MIN_ZOOM = 14;

export interface FocusableMap {
  getZoom(): number;
  easeTo(options: { center: [number, number]; zoom: number; duration?: number; offset?: [number, number] }): void;
}

/**
 * True when the map was moved. Never throws. `offset` (px) puts the point
 * that far from the map's centre; none is sent when it's [0, 0].
 */
export function centreMapOn(
  map: FocusableMap | null | undefined,
  point: { latitude: number; longitude: number },
  minZoom = FOCUS_MIN_ZOOM,
  offset: [number, number] = [0, 0],
): boolean {
  if (!map || !Number.isFinite(point?.latitude) || !Number.isFinite(point?.longitude)) return false;
  try {
    const zoom = Math.max(Number(map.getZoom()) || 0, minZoom);
    const shifted = offset[0] !== 0 || offset[1] !== 0;
    map.easeTo({ center: [point.longitude, point.latitude], zoom, duration: 600, ...(shifted ? { offset } : {}) });
    return true;
  } catch (error) {
    console.warn('[route_alerts] could not centre the map', error);
    return false;
  }
}

/**
 * A hazard whose details panel the map follows: centred, or on a phone, in the
 * middle of the map left above the panel's capped bottom sheet.
 */
export function centreAbovePanel(map: FocusableMap | null | undefined, point: { latitude: number; longitude: number }): boolean {
  const mapEl = typeof document === 'undefined' ? null : document.querySelector('.mapboxgl-map');
  const offset = followingFocusOffset({
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    mapRect: mapEl ? mapEl.getBoundingClientRect() : null,
  });
  return centreMapOn(map, point, FOCUS_MIN_ZOOM, offset);
}
